import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useEvaVoice } from "./EvaPlayButton";

/**
 * EvaVoicePicker — small pill that opens a popover of selectable Eva voices.
 * Lives inside the phone-card frame so it reads as part of the demo.
 */
export function EvaVoicePicker() {
  const { voiceId, setVoiceId, voices } = useEvaVoice();
  const [open, setOpen] = useState(false);
  const current = voices.find((v) => v.id === voiceId) ?? voices[voices.length - 1];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Eva voice — currently ${current.label}. Tap to change.`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 10px 5px 10px",
            borderRadius: 999,
            border: "1px solid rgba(196,130,58,0.35)",
            background: open ? "rgba(196,130,58,0.14)" : "rgba(255,255,255,0.92)",
            color: "rgba(140,80,20,1)",
            fontSize: 11.5,
            fontFamily: "Barlow, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            letterSpacing: "0.04em",
            lineHeight: 1,
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            transition: "background 180ms ease",
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M3 10v4M7 7v10M11 4v16M15 8v8M19 11v2" />
          </svg>
          <span>Voice · {current.label.replace(" (Default)", "")}</span>
          <svg width="9" height="6" viewBox="0 0 9 6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
            <path d="M1 1l3.5 4L8 1" />
          </svg>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        sideOffset={8}
        className="w-[260px] p-0"
        style={{
          background: "#FFFDF8",
          border: "1px solid rgba(196,130,58,0.25)",
          boxShadow: "0 12px 32px -8px rgba(60,30,0,0.18), 0 2px 6px rgba(0,0,0,0.06)",
          borderRadius: 14,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "10px 12px 6px", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(140,80,20,0.7)", fontFamily: "Barlow, sans-serif", fontWeight: 600 }}>
          Eva's voice
        </div>
        <div role="listbox" aria-label="Eva voice options" style={{ display: "flex", flexDirection: "column", padding: "2px 6px 8px" }}>
          {voices.map((v) => {
            const selected = v.id === voiceId;
            return (
              <button
                key={v.id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => { setVoiceId(v.id); setOpen(false); }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "9px 10px",
                  borderRadius: 8,
                  border: 0,
                  background: selected ? "rgba(196,130,58,0.12)" : "transparent",
                  color: "#2a1a08",
                  fontFamily: "Barlow, sans-serif",
                  fontSize: 13,
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background 140ms ease",
                }}
                onMouseEnter={(e) => { if (!selected) (e.currentTarget as HTMLButtonElement).style.background = "rgba(196,130,58,0.06)"; }}
                onMouseLeave={(e) => { if (!selected) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
              >
                <span style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: selected ? 600 : 500 }}>{v.label}</span>
                  {v.note && (
                    <span style={{ fontSize: 10.5, color: "rgba(110,80,40,0.7)", letterSpacing: "0.02em" }}>{v.note}</span>
                  )}
                </span>
                {selected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(140,80,20,1)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ padding: "8px 12px 12px", borderTop: "1px solid rgba(196,130,58,0.15)", fontSize: 11, lineHeight: 1.45, color: "rgba(80,55,30,0.85)", fontFamily: "Barlow, sans-serif" }}>
          8 prebuilt voices. With our ElevenLabs integration plus built-in voice cloning, STT and TTS, your agent can sound like anyone — instantly, free.
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EvaVoicePicker;